// ============================================================================
// Stop Service — the presentational parts, and the hint→field map.
//
// WHY THIS IS A SEPARATE FILE
// ---------------------------
// TCSD-0049: "a fix nobody can look at is not verified." The page itself
// imports the Supabase client at module scope, so it cannot be imported into
// a node test — which would leave the copy on this screen unverifiable, and
// the copy is the whole point. Every piece here is pure: no client, no auth,
// no router. `stop-service.test.tsx` renders them with renderToStaticMarkup
// and reads the sentences a person would actually see.
//
// Two rules this file exists to hold still:
//   1. "Suspended" is a DATABASE word. Nobody outside the database says it.
//      The screen says "Paused". The chip is the only thing allowed to make
//      that translation, so there is exactly one place it can drift.
//   2. Every HINT token the three lifecycle RPCs can raise has a home. An
//      unmapped token is a real sentence from the database landing on the
//      wrong field, which reads as the form ignoring you.
// ============================================================================
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check } from 'lucide-react';
import { Button } from '../../shared/components';
import { describeError } from '../../shared/errors';
import type { ClientRateDisplay } from '../../shared/rate-basis';

export type Branch = 'pause' | 'terminate';

export type FormField =
  | 'branch'
  | 'pauseReason'
  | 'resumeDate'
  | 'note'
  | 'intent'
  | 'attribution'
  | 'endReason'
  | 'finalDate'
  | 'form';

export interface LifecycleReason {
  reason_code: string;
  label: string;
  default_intent: string | null;
  default_attribution: string | null;
}

export interface OpenTermination {
  termination_id: string;
  status: string | null;
  scope: string | null;
  facility_ids: string[] | null;
  final_service_date: string | null;
}

/**
 * Whether a termination covers this facility: it names it, or it is
 * account-wide. One definition for the page, the panel and the facility card,
 * because "the account's latest termination" is how Denver's date ended up on
 * Mile High's page (2026-10-13).
 */
export function terminationCoversFacility(
  row: { scope: string | null; facility_ids: string[] | null },
  facilityId: string | null | undefined,
): boolean {
  if (!facilityId) return false;
  return row.scope === 'account' || (row.facility_ids ?? []).includes(facilityId);
}

/**
 * The same test as a PostgREST `.or()` filter, for reads that should only
 * return terminations covering one facility.
 */
export function coversFacilityFilter(facilityId: string): string {
  return `scope.eq.account,facility_ids.cs.{${facilityId}}`;
}

/**
 * Every HINT token the SQL behind this screen can raise.
 *
 * Read off the function bodies of rpc_pause_facility, rpc_resume_facility,
 * rpc_end_facility and rpc_create_account_termination on 2026-08-20. Kept as
 * data so the test can assert that FIELD_BY_HINT covers all of it — when a new
 * guard is added to the SQL, the test fails until this list and the map below
 * both learn about it.
 */
export const RPC_HINT_TOKENS = [
  // rpc_pause_facility
  'TCS_PAUSE_FORBIDDEN',
  'TCS_PAUSE_NO_FACILITY',
  'TCS_PAUSE_ALREADY_PAUSED',
  'TCS_PAUSE_ALREADY_ENDED',
  'TCS_PAUSE_FACILITY_CLOSED',
  'TCS_PAUSE_REASON_IS_END',
  'TCS_PAUSE_BAD_REASON',
  'TCS_PAUSE_NEEDS_DATE_OR_NOTE',
  'TCS_PAUSE_RESUME_DATE_PAST',
  'TCS_PAUSE_BAD_ATTRIBUTION',
  'TCS_PAUSE_BAD_INTENT',
  // rpc_resume_facility
  'TCS_RESUME_FORBIDDEN',
  'TCS_RESUME_NO_FACILITY',
  'TCS_RESUME_NOT_PAUSED',
  'TCS_RESUME_FACILITY_CLOSED',
  'TCS_RESUME_NO_SNAPSHOT',
  // rpc_end_facility
  'TCS_END_NO_FACILITY',
  'TCS_END_NO_ACCOUNT',
  'TCS_END_REASON_IS_PAUSE',
  'TCS_END_BAD_REASON',
  'TCS_END_BAD_ATTRIBUTION',
  // rpc_create_account_termination, which rpc_end_facility delegates to
  'TCS_TERM_FORBIDDEN',
  'TCS_TERM_FORBIDDEN_ROLE',
  'TCS_TERM_ACCOUNT_NOT_FOUND',
  'TCS_TERM_ACCOUNT_CLOSED',
  'TCS_TERM_ALREADY_OPEN',
  'TCS_TERM_BAD_SCOPE',
  'TCS_TERM_NO_REASON',
  'TCS_TERM_NO_FINAL_DATE',
  'TCS_TERM_BAD_END_REASON',
  'TCS_TERM_NO_FACILITIES',
  'TCS_TERM_FACILITY_NOT_ON_ACCOUNT',
  'TCS_TERM_FACILITY_ALREADY_CLOSED',
  'TCS_TERM_ACCOUNT_HAS_NO_LIVE_FACILITIES',
] as const;

/**
 * Which form field owns each server-side rejection.
 *
 * The RPCs put a stable machine token in the error's HINT and a finished human
 * sentence in the MESSAGE (PLAYBOOK §11 / TCSD-0040). This map only decides
 * WHERE to hang that sentence; the sentence itself is never rewritten here.
 * 'form' means "no single field owns this" — a banner, not silence.
 */
export const FIELD_BY_HINT: Record<string, FormField> = {
  TCS_PAUSE_FORBIDDEN: 'form',
  TCS_PAUSE_NO_FACILITY: 'form',
  TCS_PAUSE_ALREADY_PAUSED: 'form',
  TCS_PAUSE_ALREADY_ENDED: 'form',
  TCS_PAUSE_FACILITY_CLOSED: 'form',
  TCS_PAUSE_REASON_IS_END: 'pauseReason',
  TCS_PAUSE_BAD_REASON: 'pauseReason',
  TCS_PAUSE_NEEDS_DATE_OR_NOTE: 'resumeDate',
  TCS_PAUSE_RESUME_DATE_PAST: 'resumeDate',
  TCS_PAUSE_BAD_ATTRIBUTION: 'attribution',
  TCS_PAUSE_BAD_INTENT: 'intent',
  TCS_RESUME_FORBIDDEN: 'form',
  TCS_RESUME_NO_FACILITY: 'form',
  TCS_RESUME_NOT_PAUSED: 'form',
  TCS_RESUME_FACILITY_CLOSED: 'form',
  TCS_RESUME_NO_SNAPSHOT: 'form',
  TCS_END_NO_FACILITY: 'form',
  TCS_END_NO_ACCOUNT: 'form',
  TCS_END_REASON_IS_PAUSE: 'endReason',
  TCS_END_BAD_REASON: 'endReason',
  TCS_END_BAD_ATTRIBUTION: 'attribution',
  TCS_TERM_FORBIDDEN: 'form',
  TCS_TERM_FORBIDDEN_ROLE: 'form',
  TCS_TERM_ACCOUNT_NOT_FOUND: 'form',
  TCS_TERM_ACCOUNT_CLOSED: 'form',
  TCS_TERM_ALREADY_OPEN: 'form',
  TCS_TERM_BAD_SCOPE: 'form',
  TCS_TERM_NO_REASON: 'endReason',
  TCS_TERM_NO_FINAL_DATE: 'finalDate',
  TCS_TERM_BAD_END_REASON: 'endReason',
  TCS_TERM_NO_FACILITIES: 'form',
  TCS_TERM_FACILITY_NOT_ON_ACCOUNT: 'form',
  TCS_TERM_FACILITY_ALREADY_CLOSED: 'form',
  TCS_TERM_ACCOUNT_HAS_NO_LIVE_FACILITIES: 'form',
};

export function fieldErrorFrom(
  err: unknown,
  fallback: string,
): { field: FormField; message: string } {
  const d = describeError(err, fallback);
  console.error('[stop-service]', d.message, {
    code: d.code,
    hint: d.hint,
    constraint: d.constraint,
    detail: d.detail,
    raw: err,
  });
  return { field: (d.hint ? FIELD_BY_HINT[d.hint] : undefined) ?? 'form', message: d.message };
}

// ============================================================================
// What this screen is allowed to offer, derived once.
//
// The page and the entry card on the facility detail both need to know whether
// this facility can be paused, resumed or terminated. Deriving it twice is how
// the card ends up saying "Stop service" on a facility the page then refuses to
// act on. It is a pure function so the branching — the part most likely to be
// subtly wrong — is testable without a database.
//
// It answers what to OFFER, never what is permitted: the RPCs re-check all of
// it (TCS_PAUSE_FACILITY_CLOSED, TCS_RESUME_NOT_PAUSED, TCS_TERM_ALREADY_OPEN
// and the rest) and they are the authority. This only avoids rendering a
// control whose only possible outcome is an error.
// ============================================================================
export interface StopServiceCapabilities {
  /** service_state, with NULL resolved to 'Active'. */
  serviceState: string;
  isPaused: boolean;
  isEnded: boolean;
  /** status is Closed or Merged — the record itself is not live. */
  isRecordClosed: boolean;
  canOfferPause: boolean;
  canOfferResume: boolean;
  canOfferTerminate: boolean;
  /** Why terminate is not on offer, in words, or null when it is. */
  terminateBlockedReason: string | null;
}

export function deriveStopServiceCapabilities(input: {
  status: string | null | undefined;
  serviceState: string | null | undefined;
  /** Role permits pausing/resuming (any TCS employee). */
  rolePause: boolean;
  /** Role permits opening a termination (AM / national AM / exec / superuser). */
  roleTerminate: boolean;
  /**
   * An open termination that already covers THIS facility (named in it, or an
   * account-wide one), if any. A termination on a SIBLING facility does not
   * block this one: since 2026-10-13 the rule is one open termination per
   * facility, not per account (Ben: "Fix the rule properly").
   */
  openTerminationId: string | null;
}): StopServiceCapabilities {
  const serviceState = input.serviceState ?? 'Active';
  const isPaused = serviceState === 'Suspended';
  const isEnded = serviceState === 'Ended';
  const isRecordClosed = input.status === 'Closed' || input.status === 'Merged';

  // A closed or merged record is terminal on both axes. Nothing is offered —
  // not even resume, because resuming service on a Closed row would leave
  // Closed/Active, a state no screen can read (TCS_RESUME_FACILITY_CLOSED).
  const terminal = isRecordClosed;

  let terminateBlockedReason: string | null = null;
  if (terminal) {
    terminateBlockedReason = `This facility is ${input.status}. There is nothing left to terminate.`;
  } else if (isEnded) {
    terminateBlockedReason = 'Service is already recorded as ended.';
  } else if (!input.roleTerminate) {
    terminateBlockedReason =
      'Only an account manager, national AM or executive can open a termination.';
  } else if (input.openTerminationId) {
    // Guaranteed TCS_TERM_ALREADY_OPEN. Offering the button anyway is a dead
    // end; the panel below is where that termination is acknowledged or
    // cancelled.
    terminateBlockedReason = `${input.openTerminationId} already covers this facility. Each facility can be in only one open termination — acknowledge or cancel it below.`;
  }

  return {
    serviceState,
    isPaused,
    isEnded,
    isRecordClosed,
    canOfferPause: !terminal && !isPaused && !isEnded && input.rolePause,
    canOfferResume: !terminal && isPaused && input.rolePause,
    canOfferTerminate: terminateBlockedReason === null,
    terminateBlockedReason,
  };
}

export const INPUT_CLASS =
  'w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text';

export const INTENT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Retained', label: 'Retained — we expect to keep them' },
  { value: 'At_Risk', label: 'At risk — the relationship is in question' },
  { value: 'Lost', label: 'Lost — the relationship is over' },
];

export const ATTRIBUTION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'Client_Choice', label: "Client's choice" },
  { value: 'TCS_Performance', label: 'TCS performance' },
  { value: 'External', label: 'External (outside anyone’s control)' },
  { value: 'Administrative', label: 'Administrative / record-keeping' },
];

// The two sentences that carry the whole point of the screen. Constants, not
// inline JSX, so the test can assert them: pause must promise the rate, the
// crew AND the relationship survive; terminate must say the close is scheduled
// rather than immediate, and must not claim to touch the account.
export const PAUSE_BRANCH_BODY =
  'The crew stands down and Finance stops billing, but the rate, the sub’s assignment and the relationship record all survive. Reversible in one click. This is the common case.';

export const TERMINATE_BRANCH_BODY =
  'Opens a termination. Finance and HR acknowledge, an executive approves, and the facility closes on the final service date — not today. The relationship is recorded as ended.';

/**
 * What a pause does to the money, in one sentence that stays true for all
 * three rate bases.
 *
 * The first draft said "The rate of —/mo is held so service resumes at the same
 * price" on Renown Regional, a hospital billed on actual hours whose
 * monthly_rate is NULL by design. That sentence asserted a held rate that does
 * not exist, and printed an em dash with "/mo" glued to it. TCSD-0049: where a
 * field can be empty for more than one reason, the render must distinguish
 * them.
 */
export function pausedRateSentence(rate: ClientRateDisplay): string {
  const tail = 'The sub’s assignment is Suspended, not Ended.';
  if (rate.isAmount) {
    return `The rate of ${rate.text} is held so service resumes at the same price, and ${tail.charAt(0).toLowerCase()}${tail.slice(1)}`;
  }
  if (rate.basis === 'hourly') {
    return `${rate.text} — a pause changes nothing about how this building is billed, because its revenue comes from its billing lines rather than a monthly figure. ${tail}`;
  }
  return `${rate.text} — so there is no rate to hold, and the pause does not create one. ${tail}`;
}

export const fmtMoney = (n: number | null | undefined) =>
  n === null || n === undefined
    ? '—'
    : `$${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// ============================================================================
// Planned vs actual, on one line, because Finance needs both for different
// reasons.
//
// Ben, 2026-08-21: "Include a planned and Actual pause/termination date in the
// visual on the card, so finance can track for planning purposes and for
// actually paying the sub."
//
// Those are two different questions and they have two different answers:
//
//   PLANNED  — what we told the client and the sub. Drives forecasting: the
//              month a facility drops out of the book, the month a sub stops
//              being owed. Known in advance, and often wrong.
//   ACTUAL   — what happened. Drives PAYMENT: a sub is paid for the days they
//              actually worked, so "planned to end on the 30th" is not a
//              basis for a cheque.
//
// A single "date" column cannot answer both, and collapsing them is how a sub
// gets paid to a forecast. Every row is tagged with which one it is.
// ============================================================================
export type LifecycleDateKind = 'planned' | 'actual';

export interface LifecycleDateRow {
  label: string;
  /** ISO date, or null when the thing has not happened / was not set. */
  value: string | null;
  kind: LifecycleDateKind;
  /** What a null means HERE — never a bare blank (TCSD-0049). */
  emptyMeans: string;
}

/**
 * The dated facts worth showing for a facility's service state.
 *
 * Returns [] for a normally-serviced facility: there is no pause and no
 * termination, so there is nothing dated to report and a card full of dashes
 * teaches people to stop reading the card.
 */
export function lifecycleDateRows(input: {
  serviceState: string | null | undefined;
  status: string | null | undefined;
  /** facilities.service_state_changed_at — when the pause actually started. */
  serviceStateChangedAt: string | null | undefined;
  /** facilities.expected_resume_date — when the client SAID they'd be back. */
  expectedResumeDate: string | null | undefined;
  /** facilities.closed_date — when service actually ended. */
  closedDate: string | null | undefined;
  /** account_terminations.final_service_date — the planned last day. */
  terminationFinalServiceDate?: string | null;
  /** account_terminations.status, so a cancelled one stops claiming a date. */
  terminationStatus?: string | null;
}): LifecycleDateRow[] {
  const rows: LifecycleDateRow[] = [];
  const paused = (input.serviceState ?? 'Active') === 'Suspended';
  const termLive =
    !!input.terminationFinalServiceDate &&
    (input.terminationStatus ?? '') !== 'Cancelled';

  if (paused) {
    rows.push({
      label: 'Paused since',
      value: input.serviceStateChangedAt ?? null,
      kind: 'actual',
      emptyMeans: 'the pause pre-dates the RPC, so no timestamp was recorded',
    });
    rows.push({
      label: 'Planned return',
      value: input.expectedResumeDate ?? null,
      kind: 'planned',
      emptyMeans: 'open-ended — no return date was given',
    });
  }

  if (termLive) {
    rows.push({
      label: 'Planned last service',
      value: input.terminationFinalServiceDate ?? null,
      kind: 'planned',
      emptyMeans: 'not set — the close cannot be scheduled without one',
    });
    rows.push({
      label: 'Service actually ended',
      value: input.closedDate ?? null,
      kind: 'actual',
      emptyMeans: 'not yet — the close has not run',
    });
  } else if (input.status === 'Closed' || input.status === 'Merged') {
    // Closed with no termination record behind it. 28 Closed facilities
    // against 5 termination records, so this is the COMMON case, not the edge.
    rows.push({
      label: 'Service actually ended',
      value: input.closedDate ?? null,
      kind: 'actual',
      emptyMeans: 'closed with no recorded date — a data gap',
    });
  }

  return rows;
}

export function LifecycleDates({
  rows,
  className,
}: {
  rows: LifecycleDateRow[];
  className?: string;
}) {
  if (rows.length === 0) return null;
  return (
    <dl className={`flex flex-wrap gap-x-4 gap-y-1 ${className ?? ''}`}>
      {rows.map((r) => (
        <div key={r.label} className="flex items-baseline gap-1.5">
          <dt className="text-[11px] text-tcs-text-muted">{r.label}</dt>
          <dd
            className={`text-[11px] font-semibold ${
              r.value ? 'text-tcs-text' : 'text-tcs-text-secondary'
            }`}
            title={r.value ? undefined : r.emptyMeans}
          >
            {r.value ? fmtDate(r.value) : r.emptyMeans}
          </dd>
          <span
            className={`rounded px-1 py-px text-[9px] font-bold uppercase tracking-wide ${
              r.kind === 'planned'
                ? 'bg-st-info-bg text-st-info-fg-strong'
                : 'bg-tcs-surface text-tcs-text-secondary'
            }`}
          >
            {r.kind}
          </span>
        </div>
      ))}
    </dl>
  );
}

export const fmtDate = (iso: string | null | undefined) =>
  !iso
    ? '—'
    : new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });

/**
 * The service_state chip.
 *
 * The facility card and this page both render the same fact, and two
 * components deciding independently what "paused" looks like is how a screen
 * says Suspended in one place and Paused in another.
 *
 * Deliberately NOT added to LIFECYCLE_STYLES in design-tokens.ts: that map is
 * keyed on `status`, and putting a `service_state` fact in it would be exactly
 * the collapse of the two axes that TCSD-0003 forbids.
 *
 * Renders NOTHING when service is Active — which is 202 of 237 facilities. A
 * chip on every card is a chip nobody reads.
 */
export function ServiceStateChip({
  state,
  size = 'md',
}: {
  state: string | null | undefined;
  /**
   * 'dot' is for dense grids — the AM week grid packs several stops into one
   * table cell, where a word-sized pill would push the facility name out of
   * view. It carries the SAME title and an sr-only label, so it is a smaller
   * rendering of this component rather than a second renderer of the fact.
   */
  size?: 'dot' | 'sm' | 'md';
}) {
  const s = state ?? 'Active';
  if (s === 'Active') return null;

  const paused = s === 'Suspended';
  const label = paused ? 'Paused' : s === 'Ended' ? 'Service ended' : s;
  const title = paused
    ? 'Service is paused. The relationship, the rate and the sub’s assignment are all intact.'
    : 'Service has ended.';
  const tone = paused
    ? 'border-st-info-border bg-st-info-bg text-st-info-fg-strong'
    : 'border-st-danger-border bg-st-danger-bg text-st-danger-fg-strong';

  if (size === 'dot') {
    return (
      <span
        className={`inline-flex h-2.5 w-2.5 shrink-0 rounded-full border ${tone}`}
        title={`${label} — ${title}`}
      >
        <span className="sr-only">{label}</span>
      </span>
    );
  }

  const pad = size === 'sm' ? 'px-2 py-0.5 text-[11px]' : 'px-2.5 py-0.5 text-xs';
  return (
    <span
      className={`inline-flex items-center rounded-full border font-semibold ${pad} ${tone}`}
      title={title}
    >
      {label}
    </span>
  );
}

export function StateNotice({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'info' | 'warn' | 'danger';
  icon: ReactNode;
  title: string;
  children: ReactNode;
}) {
  const classes =
    tone === 'info'
      ? 'border-st-info-border bg-st-info-bg text-st-info-fg'
      : tone === 'warn'
        ? 'border-st-warn-border bg-st-warn-bg text-st-warn-fg'
        : 'border-st-danger-border bg-st-danger-bg text-st-danger-fg';
  return (
    <div className={`mt-4 rounded-xl border px-4 py-3 text-xs ${classes}`}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 shrink-0">{icon}</span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">{title}</p>
          <div className="mt-1 space-y-1">{children}</div>
        </div>
      </div>
    </div>
  );
}

export function BranchOption({
  name,
  checked,
  disabled,
  onSelect,
  icon,
  title,
  body,
}: {
  name: string;
  checked: boolean;
  disabled?: boolean;
  onSelect: () => void;
  icon: ReactNode;
  title: string;
  body: string;
}) {
  return (
    <label
      className={`mt-2 flex items-start gap-3 rounded-lg border p-3 text-sm ${
        disabled
          ? 'cursor-not-allowed border-tcs-border opacity-60'
          : checked
            ? 'cursor-pointer border-tcs-text bg-tcs-bg-muted'
            : 'cursor-pointer border-tcs-border hover:bg-tcs-bg-muted'
      }`}
    >
      <input
        type="radio"
        name={name}
        className="mt-1"
        checked={checked}
        disabled={disabled}
        onChange={onSelect}
      />
      <span className="mt-0.5 shrink-0 text-tcs-text-secondary">{icon}</span>
      <span className="min-w-0">
        <span className="font-medium text-tcs-text">{title}</span>
        <span className="mt-0.5 block text-xs text-tcs-text-secondary">{body}</span>
      </span>
    </label>
  );
}

/**
 * What the chosen reason implies, before it is saved.
 *
 * The RPCs default `relationship_intent` and `attribution` from the reason row,
 * so without this the AM picks a reason and silently sets a sales-team fact
 * they never saw. Showing it also makes the override below an informed choice.
 */
export function ReasonDefaults({ reason }: { reason: LifecycleReason | null }) {
  if (!reason) return null;
  const intent =
    INTENT_OPTIONS.find((o) => o.value === reason.default_intent)?.label ??
    reason.default_intent ??
    'not set';
  const attribution =
    ATTRIBUTION_OPTIONS.find((o) => o.value === reason.default_attribution)?.label ??
    reason.default_attribution ??
    'not set';
  return (
    <p className="rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-[11px] text-tcs-text-secondary">
      <span className="font-medium text-tcs-text">“{reason.label}”</span> records the relationship
      as <span className="font-medium text-tcs-text">{intent}</span>, attributed to{' '}
      <span className="font-medium text-tcs-text">{attribution}</span>.
    </p>
  );
}

export function OverridesDisclosure({
  open,
  onToggle,
  showIntent,
  intentValue,
  onIntentChange,
  attributionValue,
  onAttributionChange,
  intentError,
  attributionError,
}: {
  open: boolean;
  onToggle: () => void;
  showIntent: boolean;
  intentValue: string;
  onIntentChange: (v: string) => void;
  attributionValue: string;
  onAttributionChange: (v: string) => void;
  intentError?: string;
  attributionError?: string;
}) {
  return (
    <div className="rounded-md border border-tcs-border">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 text-left text-xs font-medium text-tcs-text-secondary hover:text-tcs-text"
      >
        {open ? '▾' : '▸'} Override the reason’s defaults (rarely needed)
      </button>
      {open && (
        <div className="grid gap-3 border-t border-tcs-border p-3 sm:grid-cols-2">
          {showIntent && (
            <label className="block text-sm">
              <span className="mb-1 block text-xs font-medium text-tcs-text">
                Relationship intent
              </span>
              <select
                value={intentValue}
                onChange={(e) => onIntentChange(e.target.value)}
                className={INPUT_CLASS}
              >
                <option value="">Use the reason’s default</option>
                {INTENT_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <FieldError message={intentError} />
            </label>
          )}
          <label className="block text-sm">
            <span className="mb-1 block text-xs font-medium text-tcs-text">Attribution</span>
            <select
              value={attributionValue}
              onChange={(e) => onAttributionChange(e.target.value)}
              className={INPUT_CLASS}
            >
              <option value="">Use the reason’s default</option>
              {ATTRIBUTION_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            <FieldError message={attributionError} />
          </label>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// The termination record's own pieces.
//
// Consolidated out of account-termination.tsx on 2026-08-21 (Ben: "Stop
// Service replaced account termination"). The pure parts live here so they can
// be rendered in a test; the stateful panel is termination-record.tsx.
//
// What did NOT come across, by Ben's decision: the create form (Stop Service
// creates), the scope editor, and the account-wide "close every facility on
// this payer" path. That last one has been used ZERO times in five termination
// records, and a big red close-everything control on the screen built to stop
// conflating accounts with facilities is the exact confusion it exists to end.
// The DATABASE still supports scope='account' (TCSD-0081) — it just has no
// button.
// ============================================================================

/** What state a termination is actually in, for a badge. */
export interface TerminationDisplay {
  label: string;
  className: string;
  /** True when the chain is done but no date has been set, which stalls it. */
  awaitingDate: boolean;
}

/**
 * Derived from the ack booleans, NOT from `status`.
 *
 * `status` was decorative until AFB-0078 and older rows still read 'Initiated'
 * after being fully approved, so trusting it would mislabel exactly the
 * records someone is trying to chase.
 */
export function deriveTerminationDisplay(row: {
  status: string | null;
  approved: boolean;
  finance_ack: boolean;
  hr_ack: boolean;
  final_service_date: string | null;
  applied_at?: string | null;
}): TerminationDisplay {
  if (row.status === 'Completed')
    return { label: 'Completed', className: 'bg-tcs-surface-strong text-tcs-text-secondary', awaitingDate: false };
  if (row.status === 'Cancelled')
    return { label: 'Cancelled', className: 'bg-tcs-surface text-tcs-text-muted', awaitingDate: false };
  // TCSD-0273: the close runs on the final service date whether or not anyone
  // has signed. An applied-but-unsigned record USED to read "Awaiting sign-off"
  // while the facility card next to it read Closed — the panel flatly
  // contradicting the card. The buildings are shut; say so, and say what is
  // actually outstanding.
  if (row.applied_at)
    return {
      label: 'Closed · sign-off outstanding',
      className: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
      awaitingDate: false,
    };
  if (row.approved && row.finance_ack && row.hr_ack)
    return {
      label: 'Fully approved',
      className: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
      // Fully approved with no final date never runs. It looks finished and is
      // stalled, which is the worst combination, so the panel says so.
      awaitingDate: !row.final_service_date,
    };
  return { label: 'Awaiting sign-off', className: 'bg-st-warn-bg-strong text-st-warn-fg-strong', awaitingDate: false };
}

/**
 * What the money line should say.
 *
 * TCSD-0049 again: a CLOSED termination has had every facility's monthly_rate
 * nulled by the close, so the sum is legitimately zero and "$0/mo at stake"
 * reads as "this client was worth nothing".
 *
 * Keyed on whether the close RAN (TCSD-0273), not on whether it was signed
 * off. Keying it on status === 'Completed' made an applied-but-unsigned
 * record read "no rate recorded", which blames the data for a rate the close
 * itself had cleared.
 */
export function atStakeLabel(total: number, closed: boolean): string {
  if (Math.round(total * 100) > 0) return `${fmtMoney(total)}/mo at stake`;
  return closed ? 'rate cleared by the close' : 'no rate recorded';
}

export function AckCell({
  label,
  done,
  by,
  date,
  canAct,
  busy,
  onAck,
  pendingHint,
}: {
  label: string;
  done: boolean;
  by: string | null;
  date: string | null;
  canAct: boolean;
  busy: boolean;
  onAck: () => void;
  /** Why this person cannot act. Never left as a bare "Pending". */
  pendingHint: string;
}) {
  return (
    <div
      className={`rounded-md border p-3 ${
        done ? 'border-st-ok-border bg-st-ok-bg' : 'border-tcs-border bg-tcs-raised'
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-tcs-text">{label}</span>
        {done && <Check className="h-4 w-4 text-st-ok-fg-strong" />}
      </div>
      {done ? (
        <div className="mt-1 text-[11px] text-tcs-text-muted">
          {by ?? 'unrecorded'} · {date ?? 'no date'}
        </div>
      ) : canAct ? (
        <button
          type="button"
          disabled={busy}
          onClick={onAck}
          className="mt-2 w-full rounded-md bg-tcs-inverse px-2 py-1.5 text-xs font-medium text-tcs-inverse-ink disabled:opacity-50"
        >
          Acknowledge
        </button>
      ) : (
        <div className="mt-1 text-[11px] text-tcs-text-muted">{pendingHint}</div>
      )}
    </div>
  );
}

export function FinalDateSetter({
  busy,
  onSet,
}: {
  busy: boolean;
  onSet: (d: string) => void;
}) {
  const [d, setD] = useState('');
  return (
    <div className="flex flex-wrap items-end gap-2">
      <label className="block text-sm">
        <span className="mb-1 block text-xs font-medium text-tcs-text">
          Final service date <span className="text-rag-red">*</span>
        </span>
        <input
          type="date"
          value={d}
          onChange={(e) => setD(e.target.value)}
          className={INPUT_CLASS}
        />
      </label>
      <Button size="sm" disabled={busy || !d} onClick={() => onSet(d)}>
        Set
      </Button>
      <span className="pb-2 text-[11px] text-tcs-text-muted">
        Nothing closes until this is set — it is the date the close runs.
      </span>
    </div>
  );
}

export function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="mt-1 flex items-start gap-1 text-xs text-rag-red">
      <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
      <span>{message}</span>
    </p>
  );
}

// ============================================================================
// The whole termination-record panel, as a pure component.
//
// `termination-record.tsx` is the shell: it reads the row, knows the role, and
// owns the four RPCs. Everything a person actually SEES is here, so the panel
// can be rendered in a node test and read. Extracting it caught a real defect
// the first time it was rendered — see `count` below.
// ============================================================================

export interface TerminationRecordRow {
  termination_id: string;
  initiated_date: string | null;
  reason: string | null;
  final_service_date: string | null;
  finance_ack: boolean;
  finance_ack_by_eid: string | null;
  finance_ack_date: string | null;
  hr_ack: boolean;
  hr_ack_by_eid: string | null;
  hr_ack_date: string | null;
  approved: boolean;
  approved_by_eid: string | null;
  approved_date: string | null;
  status: string | null;
  scope: string | null;
  facility_ids: string[] | null;
  /**
   * When the close was actually written to the facilities. REALITY, not
   * approval (TCSD-0273, Ben 2026-09-17). A record can be applied while still
   * unsigned, so this — never `status` — is what the panel asks when the
   * question is "has this already happened".
   */
  applied_at: string | null;
}

export interface TerminationScopeFacility {
  facility_id: string;
  facility_name: string | null;
  monthly_rate: number | null;
}

export type AckStep = 'finance' | 'hr' | 'approve';

/** The termination's state that decides which steps are open. */
export interface TerminationStepState {
  status: string | null;
  applied_at?: string | null;
  finance_ack: boolean;
  hr_ack: boolean;
  approved: boolean;
  final_service_date: string | null;
}

/**
 * Which of the five steps on an open termination this person can take now.
 * The panel below renders exactly these, and Ask AI's building card offers
 * exactly these (its copy in supabase/functions/am-copilot/buildings.ts is
 * proved equal by src/modules/copilot/ask-ai-buildings.test.ts). The RPCs
 * remain the authority.
 */
export function terminationStepsOpen(
  row: TerminationStepState,
  can: { canFinance: boolean; canHr: boolean; canApprove: boolean; canCancel: boolean },
): { finance: boolean; hr: boolean; approve: boolean; finalDate: boolean; cancel: boolean } {
  const signedOff = row.status === 'Completed';
  const applied = !!row.applied_at;
  return {
    finance: can.canFinance && !row.finance_ack && !signedOff,
    hr: can.canHr && !row.hr_ack && !signedOff,
    approve: can.canApprove && !row.approved && row.finance_ack && row.hr_ack && !signedOff,
    finalDate: !row.final_service_date && can.canApprove && !applied,
    cancel: can.canCancel && !applied,
  };
}

export function TerminationRecordView({
  row,
  facilities,
  payerLabel,
  reasonLabel,
  busy,
  canFinance,
  canHr,
  canApprove,
  canCancel,
  onAck,
  onSetFinalDate,
  onCancel,
}: {
  row: TerminationRecordRow;
  facilities: readonly TerminationScopeFacility[];
  /** The account's name if we have it, its id if we do not. Never blank. */
  payerLabel: string;
  reasonLabel: string | null;
  busy: boolean;
  canFinance: boolean;
  canHr: boolean;
  canApprove: boolean;
  canCancel: boolean;
  onAck: (step: AckStep) => void;
  onSetFinalDate: (isoDate: string) => void;
  onCancel: () => void;
}) {
  const display = deriveTerminationDisplay(row);
  const total = facilities.reduce((sum, f) => sum + (f.monthly_rate ?? 0), 0);
  // Two different questions, and conflating them is what made this panel
  // contradict the facility card (TCSD-0273).
  //   signedOff — is the paperwork finished? Gates the acknowledgements.
  //   applied   — have the buildings actually shut? Gates everything that
  //               edits the record, and every line written in a tense.
  const signedOff = row.status === 'Completed';
  const applied = !!row.applied_at;
  // Which steps this person can take now — one rule, shared with Ask AI's
  // building card (terminationStepsOpen below; the edge function's copy is
  // tested against it).
  const open = terminationStepsOpen(row, { canFinance, canHr, canApprove, canCancel });

  // What this record CLAIMS to close, and what we could actually look up.
  //
  // Rendering the real rows caught two defects here. The count used to come
  // from the resolved list while the plural came from the named array, so a
  // record naming two sites where only one still resolves read "Closing 1
  // named facilities" — wrong grammar, and worse, it quietly shrank the scope
  // an approver was signing off on. And a row with no list at all read
  // "Closing 0 named facilities", which says nothing about the problem.
  //
  // So the headline counts what the record NAMES, and says plainly when we
  // could not resolve all of it.
  const namedCount = row.facility_ids?.length ?? 0;
  const count = namedCount || facilities.length;
  const unresolved = namedCount > 0 ? namedCount - facilities.length : 0;
  const scopeLine =
    row.scope === 'account'
      ? `${applied ? 'Closed' : 'Closing'} every live facility on the ${payerLabel} account`
      : count === 0
        ? 'No facilities named on this record'
        : `${applied ? 'Closed' : 'Closing'} ${count} named ${count === 1 ? 'facility' : 'facilities'}${
            unresolved > 0 ? ` — ${unresolved} no longer resolvable` : ''
          }`;

  return (
    <div className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-5">
      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-tcs-text-secondary" />
        <h2 className="text-sm font-semibold text-tcs-text">
          Termination {row.termination_id} — acknowledgements and approval
        </h2>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="text-xs text-tcs-text-muted">
          Raised {fmtDate(row.initiated_date)} ·{' '}
          {applied ? 'last service' : 'planned last service'}{' '}
          {row.final_service_date ? fmtDate(row.final_service_date) : 'NOT SET'}
        </p>
        <span
          className={`rounded px-2 py-1 text-[11px] font-medium uppercase tracking-wide ${display.className}`}
        >
          {display.label}
        </span>
      </div>

      {/* What an approver is actually authorising: the sites, and the money. */}
      <div className="mt-3 rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="font-medium text-tcs-text">{scopeLine}</span>
          <span className="font-semibold tabular-nums text-tcs-text">
            {atStakeLabel(total, applied)}
          </span>
        </div>
        <ul className="mt-2 space-y-0.5">
          {facilities.map((f) => (
            <li key={f.facility_id} className="flex items-center justify-between gap-3">
              <span className="min-w-0 truncate text-tcs-text-secondary">
                {f.facility_name ?? f.facility_id}
                <span className="ml-1 text-[11px] text-tcs-text-muted">{f.facility_id}</span>
              </span>
              <span className="tabular-nums text-tcs-text-secondary">
                {f.monthly_rate == null ? 'no rate' : `${fmtMoney(f.monthly_rate)}/mo`}
              </span>
            </li>
          ))}
          {facilities.length === 0 && (
            <li className="text-tcs-text-muted">
              {namedCount > 0
                ? `Named: ${row.facility_ids?.join(', ')} — they may already be closed.`
                : row.scope === 'account'
                  ? 'This account has no live facilities left to close.'
                  : 'Nothing to close. This record names no facility, so it can only be cancelled.'}
            </li>
          )}
        </ul>
        {reasonLabel && (
          <div className="mt-2 border-t border-tcs-border pt-2 text-tcs-text-secondary">
            <span className="font-medium text-tcs-text">End reason: </span>
            {reasonLabel}
          </div>
        )}
      </div>

      {display.awaitingDate && (
        <p className="mt-3 rounded-md border border-st-warn-border bg-st-warn-bg px-3 py-2 text-xs text-st-warn-fg-strong">
          Fully approved but <strong>stalled</strong> — with no final service date the close never
          runs. Set one below.
        </p>
      )}

      {row.reason && (
        <p className="mt-3 rounded-md border border-tcs-border bg-tcs-bg-muted px-3 py-2 text-xs text-tcs-text-secondary">
          {row.reason}
        </p>
      )}

      <div className="mt-3 grid gap-3 sm:grid-cols-3">
        <AckCell
          label="Finance"
          done={row.finance_ack}
          by={row.finance_ack_by_eid}
          date={row.finance_ack_date}
          canAct={open.finance}
          busy={busy}
          pendingHint={signedOff ? 'not recorded before completion' : 'waiting on Finance'}
          onAck={() => onAck('finance')}
        />
        <AckCell
          label="HR"
          done={row.hr_ack}
          by={row.hr_ack_by_eid}
          date={row.hr_ack_date}
          canAct={open.hr}
          busy={busy}
          pendingHint={signedOff ? 'not recorded before completion' : 'waiting on HR'}
          onAck={() => onAck('hr')}
        />
        <AckCell
          label="Executive approval"
          done={row.approved}
          by={row.approved_by_eid}
          date={row.approved_date}
          canAct={open.approve}
          busy={busy}
          pendingHint={
            signedOff
              ? 'not recorded before completion'
              : !row.finance_ack || !row.hr_ack
                ? 'needs Finance and HR first'
                : 'waiting on an executive'
          }
          onAck={() => onAck('approve')}
        />
      </div>

      {/* Deliberately gated on signedOff, NOT applied: acknowledging AFTER the
          buildings have shut is the whole point of TCSD-0273. The close never
          waits for a signature, and the signature is still owed afterwards. */}

      {open.finalDate && (
        <div className="mt-3">
          <FinalDateSetter busy={busy} onSet={onSetFinalDate} />
        </div>
      )}

      {open.cancel && (
        <div className="mt-3 border-t border-tcs-border pt-3">
          <button
            type="button"
            disabled={busy}
            onClick={onCancel}
            className="text-xs text-tcs-text-secondary underline-offset-2 hover:underline disabled:opacity-50"
          >
            Cancel this termination
          </button>
        </div>
      )}
    </div>
  );
}


// ============================================================================
// Other closures on this account.
//
// Since 2026-10-13 an account can carry several open terminations at once, one
// per facility (Ben: "Fix the rule properly"). The panel above shows the one
// covering THIS facility; these rows show the rest, each linking to its own
// facility's Stop Service page where its sign-off lives. Read-only on purpose:
// acknowledging Denver from Mile High's page is how sign-offs get crossed.
// ============================================================================
export interface OtherClosureRow {
  termination_id: string;
  /** The facility whose Stop Service page carries this termination. */
  facility_id: string | null;
  facility_names: string;
  final_service_date: string | null;
  status: string | null;
  approved: boolean;
  finance_ack: boolean;
  hr_ack: boolean;
}

export function OtherClosures({ rows }: { rows: OtherClosureRow[] }) {
  if (rows.length === 0) return null;
  return (
    <section
      aria-label="Other closures on this account"
      className="mt-4 rounded-xl border border-tcs-border bg-tcs-bg p-4"
    >
      <h2 className="text-sm font-semibold text-tcs-text">Other closures on this account</h2>
      <ul className="mt-2 divide-y divide-tcs-border">
        {rows.map((r) => {
          const d = deriveTerminationDisplay(r);
          return (
            <li
              key={r.termination_id}
              className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
            >
              <span className="text-tcs-text">
                {r.facility_names}
                <span className="text-tcs-text-secondary">
                  {' '}
                  · last service {r.final_service_date ? fmtDate(r.final_service_date) : 'not set'}
                </span>
              </span>
              <span className="flex items-center gap-3">
                <span className={`rounded px-2 py-0.5 text-[11px] font-semibold ${d.className}`}>
                  {d.label}
                </span>
                {r.facility_id && (
                  <Link
                    className="text-sm underline"
                    to={`/account-management/facilities/${r.facility_id}/stop-service`}
                  >
                    Open
                  </Link>
                )}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
